/* eslint-disable no-restricted-imports */
/*
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited 2023.
 */

import { OrderItemListRows } from '@/data/Content/OrderItemList';
import { ProductType } from '@/data/types/Product';
import { mergeRibbonsForProduct } from '@/utils/mergeRibbonsForProduct';
import { findProductAttribute } from 'integration/utils/findProductAttribute';

export const getAttributeValue = (attributes: ProductType['attributes'], identifier: string) =>
	findProductAttribute(attributes, identifier)?.value;

export const getProductSizes = (product?: ProductType) =>
	product?.items
		?.map((item) => getAttributeValue(item.attributes, 'Talla') ?? '')
		.filter((size): size is string => Boolean(size)) ?? [];

export const getPriceType = (productPrice?: ProductType['productPrice']) => {
	const list = productPrice?.list;
	const offer = productPrice?.offer;

	return (offer ?? 0) > 0 && offer !== (list ?? 0) ? 'Oferta' : 'Full price';
};

export const getProductRibbons = (product?: ProductType) =>
	mergeRibbonsForProduct(product?.ribbons ?? []).flatMap((ribbon) => ribbon.value);

/**
 * Normaliza una fila de `orderItemListRows` (de useBasicCheckOut) al objeto product
 * que consumen los data files de checkout/purchase. Usa el SKU hijo y la info
 * completa del producto, independiente del contextData del evento view_cart.
 */
export const mapOrderRowToGTMProduct = (row: OrderItemListRows[number]) => {
	const attributes = row.details.attributes;
	const product = row.product;
	// `product` es el SKU hijo (fetch por partNumber) y NO trae `items`/`ribbons`;
	// los datos a nivel producto (tallas disponibles, ribbons) viven en el padre.
	const parentProduct = row.parentProduct;
	const productPrice = product?.productPrice;
	const { itemDetails, quantity } = row.orderItemListRow;

	return {
		id: row.details?.partNumber || parentProduct?.partNumber,
		type: product?.type,
		name: product?.name,
		price: Number(itemDetails?.unitPrice),
		quantity: quantity?.quantity,
		currency: itemDetails?.currency,
		affiliation: 'Dportenis',
		keyword: product?.keyword,
		shippingTier: row.orderItemListRow.orderItem?.shipModeCode,
		brand: getAttributeValue(attributes, 'Marca'),
		size: getAttributeValue(attributes, 'Talla'),
		genre: getAttributeValue(attributes, 'Genero'),
		family: getAttributeValue(attributes, 'Familia'),
		use: getAttributeValue(attributes, 'Uso'),
		color: getAttributeValue(attributes, 'Color1'),
		sizes: getProductSizes(parentProduct),
		priceType: getPriceType(productPrice),
		ribbons: getProductRibbons(parentProduct),
		productPrice,
	};
};
